import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createOrganizationUserOrganizationOrganizationIdUsersPostMutation,
  getOrganizationUsersOrganizationOrganizationIdUsersGetQueryKey,
} from '@/generated/@tanstack/react-query.gen'
import { useUser } from '@/pages/enterprise/user-provider'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import { toast } from '@/components/custom-toast'
import { isArray } from 'lodash'

const schema = z.object({
  firstname: z.string().min(1, { message: 'First name is required.' }),
  lastname: z.string().min(1, { message: 'Last name is required.' }),
  email: z.string().email({ message: 'Invalid email address.' }),
  role: z.string().refine((value) => value === '6' || value === '7', {
    message: 'Invalid role',
  }),
})

type FormData = z.infer<typeof schema>

export default function AddMemberModal() {
  const { organizationId, onUpdateSeats, availableSeats } = useUser()

  const [open, setOpen] = useState(false)

  const queryClient = useQueryClient()

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      role: '6',
    },
  })

  const addUser = useMutation({
    ...createOrganizationUserOrganizationOrganizationIdUsersPostMutation(),
    onError: (error) => {
      if (
        error.response?.status === 422 &&
        isArray(error.response.data?.detail)
      ) {
        return (error.response.data?.detail ?? []).forEach((error: any) => {
          form.setError(error.loc[1], {
            type: 'manual',
            message: error.msg,
          })
        })
      }
      toast.error({
        message: (error.response?.data.detail as any) ?? 'An error occurred',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey:
          getOrganizationUsersOrganizationOrganizationIdUsersGetQueryKey({
            path: {
              organization_id: organizationId,
            },
          }),
      })

      onUpdateSeats()
      toast.success({
        message: 'User added successfully.',
      })
      setOpen(false)
    },
  })

  useEffect(() => {
    if (!open) {
      form.reset()
    }
  }, [open, form])

  const onSubmit = (data: FormData) => {
    addUser.mutate({
      path: {
        organization_id: organizationId,
      },
      body: {
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        useraccesslevel: Number(data.role) as 6 | 7,
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger disabled={availableSeats === 0} asChild>
        <Button className="!gap-0 font-bold px-6 text-[14px] h-8 md:h-12 md:text-[16px]">
          <Plus
            className="
            w-4 h-4 mr-1 text-[14px] md:text-[16px]
            md:w-6 md:h-6 md:mr-2"
          />
          Add User
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add User</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10">
              <FormField
                control={form.control}
                name="firstname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      First Name <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="h-14"
                        placeholder="First name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastname"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Last Name <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="h-14"
                        placeholder="Last name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Email <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        className="h-14"
                        type="email"
                        placeholder="name@email.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Role <span className="text-red-500">*</span>
                    </FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="h-14">
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="6">Enterprise Member</SelectItem>
                        <SelectItem value="7">Enterprise Admin</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-start space-x-4 pt-8">
              <Button
                className="px-6"
                type="submit"
                disabled={addUser.isPending}
              >
                Add
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
